import { useState, useCallback, useMemo } from 'react';
import {
    DataGrid,
    GridRowsProp,
    GridColDef,
    DataGridProps,
    GridColumnMenuProps,
    GridColumnMenu,
    GridColumnVisibilityModel,
    GridCallbackDetails,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridPaginationModel,
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import '@/styles/components/ui/BaseDataTable.scss';

interface BaseDataTableProps extends DataGridProps {
    rows: GridRowsProp;
    columns: GridColDef[];
    rowBufferPx?: number;
    columnBufferPx?: number;
}

function CustomToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
        </GridToolbarContainer>
    );
}

// Remove sort and filter options
function CustomColumnMenu(props: GridColumnMenuProps) {
    return (
        <GridColumnMenu
            {...props}
            slots={{
                columnMenuSortItem: null,
                columnMenuFilterItem: null,
            }}
        />
    );
}

function BaseDataTable({
    rows,
    columns,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
    rowHeight = 40,
    columnHeaderHeight = 40,
    rowBufferPx = 100,
    columnBufferPx = 50,
    paginationModel,
    onPaginationModelChange,
    ...props
}: BaseDataTableProps) {
    const MIN_VISIBLE_ROWS = 5;
    const FOOTER_HEIGHT = 52;
    const computedMinHeight = columnHeaderHeight + rowHeight * MIN_VISIBLE_ROWS + FOOTER_HEIGHT;

    const [isAllColumnHidden, setIsAllColumnHidden] = useState(false);

    const handlePaginationModelChange = useCallback(
        (model: GridPaginationModel, details: GridCallbackDetails) => {
            if (onPaginationModelChange) {
                onPaginationModelChange(model, details);
            }
        },
        [onPaginationModelChange],
    );
    const columnVisibilityDefault = useMemo(() => {
        const defaultModel = {} as GridColumnVisibilityModel;
        columns.forEach((column) => {
            if (column.field === '__check__') return;
            defaultModel[column.field] = true;
        });
        return defaultModel;
    }, [columns]);

    const columnVisibilityChangeHandler = useCallback(
        (newModel: GridColumnVisibilityModel, details: GridCallbackDetails) => {
            const newColumnVisibilityModel = { ...columnVisibilityDefault, ...newModel };
            const isAllColumnHidden = Object.values(newColumnVisibilityModel).every((value) => !value);
            setIsAllColumnHidden(isAllColumnHidden);

            if (onColumnVisibilityModelChange) onColumnVisibilityModelChange(newColumnVisibilityModel, details);
        },
        [columnVisibilityDefault, onColumnVisibilityModelChange],
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: computedMinHeight,
                maxHeight: 772,
            }}
        >
            <DataGrid
                className="base-data-table"
                rows={rows}
                columns={columns}
                getRowHeight={() => 'auto'}
                getEstimatedRowHeight={() => rowHeight}
                pageSizeOptions={[5, 10, 25, 50, 100, 1000]}
                disableVirtualization={false}
                rowBufferPx={rowBufferPx}
                columnBufferPx={columnBufferPx}
                disableRowSelectionOnClick={false}
                disableMultipleRowSelection={false}
                hideFooterSelectedRowCount={false}
                disableColumnFilter={true}
                disableColumnMenu={false}
                slotProps={{
                    loadingOverlay: {
                        variant: 'linear-progress',
                        noRowsVariant: 'linear-progress',
                    },
                }}
                slots={{
                    columnMenu: CustomColumnMenu,
                    toolbar: isAllColumnHidden ? CustomToolbar : null,
                }}
                sx={{
                    display: 'grid',
                    gridTemplateRows: '1fr 52px',
                    '@keyframes dataTableRowFadeIn': {
                        from: { opacity: 0 },
                        to: { opacity: 1 },
                    },

                    '& .MuiDataGrid-cell': {
                        display: 'flex',
                        alignItems: 'center',
                        lineHeight: 'normal',
                        py: 0,
                        contain: 'layout style',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                    },
                    '& .MuiDataGrid-cellContent': {
                        width: '100%',
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.4,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        lineHeight: 'normal',
                    },
                    '& .MuiDataGrid-footerContainer': {
                        overflow: 'hidden',
                    },
                    '& .MuiDataGrid-footerContainer .MuiTablePagination-root': {
                        maxHeight: '100%',
                        overflow: 'hidden',
                        flexGrow: 0,
                    },

                    '& .MuiDataGrid-row': {
                        willChange: 'transform',
                        contain: 'layout style paint',
                        animation: 'dataTableRowFadeIn 200ms ease-out',
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        '& .MuiDataGrid-row': {
                            animation: 'none',
                        },
                    },
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={columnVisibilityChangeHandler}
                {...(paginationModel ? { paginationModel } : {})}
                onPaginationModelChange={handlePaginationModelChange}
                rowHeight={rowHeight}
                columnHeaderHeight={columnHeaderHeight}
                {...props}
            />
        </Box>
    );
}

export default BaseDataTable;
